import {
  PERMISSIONS,
  PERMISSION_LABELS,
  ROLE_DEFINITIONS,
  ROUTE_PERMISSIONS,
} from '../constants/accessControl';

const normalizePermissionList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const getRolePermissions = (role) => {
  if (!role) return [];
  return ROLE_DEFINITIONS[role]?.permissions || [];
};

export const getRoleLabel = (role) => ROLE_DEFINITIONS[role]?.label || role || 'User';

export const getEffectivePermissions = (userProfile) => {
  if (!userProfile) return [];
  const rolePermissions = getRolePermissions(userProfile.role);
  const extraPermissions = normalizePermissionList(userProfile.permissions);
  return [...new Set([...rolePermissions, ...extraPermissions])];
};

export const hasPermission = (userProfile, permission) => {
  if (!permission) return true;
  if (!userProfile) return false;
  if (userProfile.role === 'admin') return true;
  return getEffectivePermissions(userProfile).includes(permission);
};

export const hasAnyPermission = (userProfile, permissions = []) => {
  if (!permissions.length) return true;
  return permissions.some((permission) => hasPermission(userProfile, permission));
};

export const hasAllPermissions = (userProfile, permissions = []) => {
  if (!permissions.length) return true;
  return permissions.every((permission) => hasPermission(userProfile, permission));
};

export const getPermissionLabel = (permission) =>
  PERMISSION_LABELS[permission] || permission;

export const getRequiredPermissionForPath = (pathname) => {
  if (!pathname) return null;
  if (ROUTE_PERMISSIONS[pathname]) return ROUTE_PERMISSIONS[pathname];

  const basePath = pathname.split('/').slice(0, 2).join('/') || pathname;
  if (ROUTE_PERMISSIONS[basePath]) return ROUTE_PERMISSIONS[basePath];

  if (pathname.startsWith('/league-setup')) return PERMISSIONS.LEAGUE_ORGANIZER;
  if (pathname.startsWith('/coach-register/')) return null;

  return null;
};

export const canAccessPath = (userProfile, pathname) => {
  const required = getRequiredPermissionForPath(pathname);
  if (!required) return !!userProfile;
  return hasPermission(userProfile, required);
};

export const getExtraPermissionOptions = (role) => {
  const rolePermissions = new Set(getRolePermissions(role));
  return Object.entries(PERMISSION_LABELS)
    .filter(([key]) => !rolePermissions.has(key))
    .map(([value, label]) => ({ value, label }));
};

export { PERMISSIONS, PERMISSION_LABELS, ROLE_DEFINITIONS };
